import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import {
  Field,
  FieldService,
  isReservedPrefixValidator,
} from 'app/providers/storage-api/field.service';
import { noWhitespaceValidator, jsonValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

import { CustomFieldsService } from '../custom-fields.service';
import { FieldAliasSelectorComponent } from '../field-alias-selector/field-alias-selector.component';

export interface FieldResult {
  name: string;
  type: string;
  filterable: boolean;
  containsPII: boolean;
  alias?: boolean;
  subtype?: string;
  includedFields?: string[];
  rating?: boolean;
  calculationType?: string;
  calculationDefinition?: string;
}
@Component({
  selector: 'app-field-create-modal',
  templateUrl: './field-create-modal.component.html',
  styleUrls: ['./field-create-modal.component.scss'],
  imports: [AppSharedModule, FieldAliasSelectorComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FieldCreateModalComponent implements OnInit, OnDestroy {
  @Input() fields: Field[];
  @Input() preSelectedField?: Field;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private transloco: TranslocoService,
    private accountService: SemantriaAccountService,
    private cfService: CustomFieldsService
  ) {}

  fieldCreateForm: FormGroup;
  fieldTypes: DropdownObject[];
  fieldSubTypes: DropdownObject[];
  selectedSubType: any;
  fieldNameStatus: ErrorObject = ErrorObjectUtils.create();
  calcDefStatus: ErrorObject = ErrorObjectUtils.create();
  fieldNameMaxLength: number;
  includedFieldsValid: boolean;
  calcTypes: DropdownObject[];

  aliasSelected = false;
  includFieldColumns = ['fieldName', 'include'];
  includedFieldsDataSource: MatTableDataSource<Field>;
  includedFields: string[];
  filteredFields: Field[];
  translations: any;
  defaultSubtype = 'keyword';

  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.translations = this.transloco.translateObject('field-create-dialog');
    const ft = this.translations['field-types'];
    this.fieldSubTypes = [
      { title: ft['keyword'], id: 'keyword' },
      { title: ft['long'], id: 'long' },
      { title: ft['date'], id: 'date' },
      { title: ft['double'], id: 'double' },
    ];
    this.fieldTypes = [
      { title: ft['alias'], id: 'alias' },
      { title: ft['calculated'], id: 'calculated' },
      ...this.fieldSubTypes,
    ];

    this.calcTypes = [
      { title: this.translations['calculation-types']['score'], id: 'score' },
      { title: this.translations['calculation-types']['math'], id: 'math' },
      { title: this.translations['calculation-types']['average'], id: 'average' },
      { title: this.translations['calculation-types']['repercentage'], id: 'repercentage' },
      { title: this.translations['calculation-types']['rescore'], id: 'rescore' },
      { title: this.translations['calculation-types']['weighted-index'], id: 'weighted-index' },
      { title: this.translations['calculation-types']['age'], id: 'age' },
      { title: this.translations['calculation-types']['binary'], id: 'binary' },
    ];

    this.fieldCreateForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(FieldService.FIELD_VALID_RE),
          noWhitespaceValidator,
          this.isDuplicateFieldValidator(),
          isReservedPrefixValidator,
        ],
      ],
      type: ['', Validators.required],
      subtype: [],
      isFilterable: [true],
      containsPII: [false],
      calcType: [''],
      calcDef: [''],
    });

    if (this.preSelectedField) {
      this.type.setValue('alias');
      this.aliasSelected = true;
      this.subtype.setValue(this.preSelectedField.fieldType);
      this.selectedSubType = this.preSelectedField.fieldType;
      this.type.disable();
    }

    this.fieldCreateForm.controls.name.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((status) => {
        this.fieldNameStatus = ErrorObjectUtils.create();
        const errors = this.fieldCreateForm.controls.name.errors;
        if (status === 'INVALID' && errors) {
          switch (Object.keys(errors)[0]) {
            case 'required':
            case 'whitespace':
              ErrorObjectUtils.setError(
                this.fieldNameStatus,
                this.translations['field-name-required']
              );
              break;
            case 'duplicate':
              ErrorObjectUtils.setError(
                this.fieldNameStatus,
                this.translations['field-name-already-in-use']
              );
              break;
            case 'pattern':
              ErrorObjectUtils.setError(
                this.fieldNameStatus,
                this.translations['field-name-invalid']
              );
              break;
            case 'maxlength':
              ErrorObjectUtils.setError(
                this.fieldNameStatus,
                this.transloco.translate('field-create-dialog.field-name-max-length', {
                  maxLength: this.fieldNameMaxLength,
                })
              );
              break;
            case 'reserved':
              ErrorObjectUtils.setError(
                this.fieldNameStatus,
                this.transloco.translate('field-create-dialog.reserved-prefix')
              );
              break;
          }
        }
      });

    this.fieldCreateForm.controls.calcDef.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((status) => {
        this.calcDefStatus = ErrorObjectUtils.create();
        const errors = this.fieldCreateForm.controls.calcDef.errors;
        if (status !== 'INVALID' || !errors) {
          return;
        }

        ErrorObjectUtils.setError(this.calcDefStatus, this.translations['invalid-json']);
      });

    this.fieldCreateForm
      .get('type')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        if (value === 'alias') {
          this.subtype.patchValue(this.defaultSubtype);
          this.subtype.enable();
          this.containsPII.disable();
          this.aliasSelected = true;
        } else {
          this.subtype.disable();
          this.containsPII.enable();
          this.containsPII.setValue(false);
          this.aliasSelected = false;
        }

        if (this.isCalculatedField) {
          this.fieldCreateForm.controls.calcType.setValidators(Validators.required);
          this.fieldCreateForm.controls.calcDef.setValidators(
            Validators.compose([Validators.required, jsonValidator()])
          );
        } else {
          this.fieldCreateForm.controls.calcType.clearValidators();
          this.fieldCreateForm.controls.calcDef.clearValidators();
        }

        this.fieldCreateForm.controls.calcType.updateValueAndValidity();
        this.fieldCreateForm.controls.calcDef.updateValueAndValidity();
      });

    this.subtype.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.selectedSubType = value;
      this.includedFields = [];
      this.includedFieldsValid = false;
    });

    this.setMaxLengthValidator();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  //If new field has duplicate label OR normalized name
  private isDuplicateFieldValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const normalizedName = control.value
        .toLowerCase()
        .replace(FieldService.NORMALIZED_NAME_RE, '');
      for (const field of this.fields) {
        if (
          field.label.toLowerCase() === control.value.toLowerCase() ||
          field.normalizedName === normalizedName
        ) {
          return { duplicate: true };
        }
      }
    };
  }

  get type() {
    return this.fieldCreateForm.get('type');
  }

  get subtype() {
    return this.fieldCreateForm.get('subtype');
  }

  get containsPII() {
    return this.fieldCreateForm.get('containsPII');
  }

  get containsPIITooltip(): string | null {
    if (!this.aliasSelected) {
      return null;
    }
    return this.containsPII.value === true
      ? this.translations['fields-contain-pii']
      : this.translations['no-fields-contain-pii'];
  }

  get isCalculatedField(): boolean {
    return this.fieldCreateForm.controls.type.value === 'calculated';
  }

  private async setMaxLengthValidator() {
    const accountLimits = (await this.accountService.sub.latest()).limits;
    this.fieldNameMaxLength =
      accountLimits?.SPOTLIGHT_MAX_SHORT_FIELD_LENGTH ??
      Constants.SPOTLIGHT_MAX_SHORT_FIELD_DEFAULT_LENGTH;
    this.fieldCreateForm.controls['name'].addValidators([
      Validators.maxLength(this.fieldNameMaxLength),
    ]);
  }

  onAliasSelectionChange(value: {
    includedFields: string[];
    filteredFields: Field[];
    containsPII: boolean;
  }) {
    this.includedFields = value.includedFields;
    this.includedFieldsValid = this.includedFields.length > 0;
    this.filteredFields = value.filteredFields;
    this.containsPII.setValue(value.containsPII);
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  onCreateField() {
    if (this.fieldCreateForm.valid) {
      const controls = this.fieldCreateForm.controls;

      const toEmit: FieldResult = {
        name: controls.name.value.trim(),
        type: controls.type.value,
        filterable: controls.isFilterable.value,
        containsPII: controls.containsPII.value,
      };

      if (this.aliasSelected) {
        toEmit.type = controls.subtype.value;
        toEmit.alias = true;
        toEmit.includedFields = this.includedFields;
        if (toEmit.type === 'double' || toEmit.type === 'long') {
          toEmit.rating = this.cfService.anyFieldContainsValue(
            this.includedFields,
            this.filteredFields,
            'rating'
          );
        }
      }

      if (this.isCalculatedField) {
        toEmit.calculationType = controls.calcType.value;
        toEmit.calculationDefinition = controls.calcDef.value;
      }

      this.modalClosed.emit(toEmit);
    }
  }
}
